class WonderWallMobileNavigation extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.dialog = this.querySelector('dialog');
    this.trigger = this.querySelector('[data-ww-menu-trigger]');
    this.body = this.querySelector('[data-ww-menu-body]');
    this.heading = this.querySelector('[data-ww-menu-heading]');
    this.stack = ['root'];
    this.scrollPositions = new Map();
    this.trigger.addEventListener('click', () => this.open(), options);
    this.querySelector('[data-ww-menu-close]').addEventListener('click', () => this.close(), options);
    this.querySelector('[data-ww-menu-back]').addEventListener('click', () => history.back(), options);
    this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(); }, options);
    this.dialog.addEventListener('keydown', event => {
      if (event.key !== 'Tab') return;
      const targets = [...this.dialog.querySelectorAll('a[href], button, input, select, textarea, [tabindex]')]
        .filter(element => !element.disabled && element.tabIndex >= 0 && element.getClientRects().length);
      const first = targets[0], last = targets.at(-1);
      if (!first) { event.preventDefault(); this.heading.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || document.activeElement === this.heading)) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    }, options);
    this.dialog.addEventListener('click', event => {
      const button = event.target.closest('[data-ww-panel-next]');
      if (button) this.navigate(button.dataset.wwPanelNext);
    }, options);
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-ww-open-menu-panel]');
      if (!button || !this.ownsNavigation()) return;
      const discovery = ['brands','edits'].includes(button.dataset.wwOpenMenuPanel);
      if (!this.isActive() && !discovery) return;
      event.preventDefault();
      this.open(button.dataset.wwOpenMenuPanel);
    }, options);
    window.addEventListener('popstate', event => {
      if (!this.ownsNavigation()) { this.finishClose(false); return; }
      if (this.closingHistory) {
        this.closingHistory = false;
        requestAnimationFrame(() => {
          if (!this.dialog.open) window.scrollTo({ top: this.pageScroll, behavior: 'instant' });
        });
        const queued = this.queuedOpen;
        this.queuedOpen = null;
        if (queued) this.open(queued);
        return;
      }
      if (this.dialog.open) this.scrollPositions.set(this.stack.at(-1), this.body.scrollTop);
      const state = event.state?.wwMobileMenu;
      if (!matchMedia('(max-width:749px)').matches && ['brands','edits'].includes(state?.stack?.at(-1))) this.desktopDiscovery = true;
      if (this.isActive() && state?.path === location.pathname && Array.isArray(state.stack)) {
        this.stack = state.stack.filter(panel => this.dialog.querySelector(`[data-ww-menu-panel="${CSS.escape(panel)}"]`));
        if (!this.stack.length) this.stack = ['root'];
        this.show();
        this.render();
      } else this.finishClose();
    }, options);
    window.addEventListener('pagehide', () => this.finishClose(false), options);
    const sizeViewport = () => this.dialog.style.setProperty('--ww-menu-height', `${window.visualViewport?.height || window.innerHeight}px`);
    window.visualViewport?.addEventListener('resize', sizeViewport, options);
    sizeViewport();
    window.addEventListener('resize', () => {
      sizeViewport();
      if (this.dialog.open && !this.isActive()) this.close();
    }, options);
    this.headerObserver?.disconnect();
    this.headerObserver = new MutationObserver(() => {
      if (this.dialog.open && !this.isActive()) this.close();
    });
    const header = this.closest('#header-component');
    if (header) this.headerObserver.observe(header, { attributes:true, attributeFilter:['data-menu-style'] });
  }

  disconnectedCallback() {
    this.finishClose(false);
    this.controller?.abort();
    this.headerObserver?.disconnect();
  }

  ownsNavigation() {
    const instances = [...document.querySelectorAll('ww-mobile-navigation')].filter(e=>e.isConnected && e.dialog && e.trigger);
    return (instances.find(e=>e.trigger.getClientRects().length) || instances[0]) === this;
  }

  isActive() {
    return matchMedia('(max-width:749px)').matches || this.closest('#header-component')?.dataset.menuStyle === 'drawer' || this.desktopDiscovery === true;
  }

  pushState() {
    history.pushState({ ...history.state, wwMobileMenu: { path: location.pathname, stack: [...this.stack] } }, '', location.href);
  }

  open(panel = 'root') {
    if (!matchMedia('(max-width:749px)').matches && this.closest('#header-component')?.dataset.menuStyle !== 'drawer' && ['brands','edits'].includes(panel)) this.desktopDiscovery = true;
    if (!this.isActive()) return;
    if (this.closingHistory) { this.queuedOpen = panel; return; }
    if (this.dialog.open) return;
    this.returnFocus = document.activeElement;
    this.stack = ['root'];
    this.pushState();
    this.show();
    this.render();
    if (panel !== 'root') this.navigate(panel);
  }

  show() {
    if (this.dialog.open) return;
    this.pageScroll = window.scrollY;
    this.savedBody = {};
    for (const key of ['position', 'top', 'left', 'right', 'width', 'overflow']) this.savedBody[key] = document.body.style[key];
    // Keep the modal outside Safari's filtered/sticky header compositing layer.
    this.dialogAnchor = document.createComment('ww-menu-dialog');
    this.dialog.before(this.dialogAnchor);
    const inherited = getComputedStyle(this);
    for (const name of inherited) {
      if (name.startsWith('--')) this.dialog.style.setProperty(name, inherited.getPropertyValue(name));
    }
    document.body.append(this.dialog);
    Object.assign(document.body.style, {position:'fixed',top:`-${this.pageScroll}px`,left:'0',right:'0',width:'100%',overflow:'hidden'});
    try {
      this.dialog.toggleAttribute('data-ww-desktop-discovery', !!this.desktopDiscovery);
      this.dialog.showModal();
    } catch (error) {
      // A failed modal must never leave the page scroll-locked.
      this.restorePage();
      throw error;
    }
    this.trigger.setAttribute('aria-expanded', 'true');
    if (this.returnFocus?.matches('[data-ww-open-menu-panel]')) this.returnFocus.setAttribute('aria-expanded','true');
  }

  navigate(panel) {
    if (!this.dialog.querySelector(`[data-ww-menu-panel="${CSS.escape(panel)}"]`)) return;
    this.scrollPositions.set(this.stack.at(-1), this.body.scrollTop);
    this.stack.push(panel);
    this.pushState();
    this.render();
  }

  render() {
    const current = this.stack.at(-1);
    this.dialog.querySelector('[data-ww-menu-logo]').hidden = current !== 'root';
    this.heading.classList.toggle('ww-mobile-menu__heading--root', current === 'root');
    this.dialog.querySelectorAll('[data-ww-menu-panel]').forEach(panel => {
      panel.hidden = panel.dataset.wwMenuPanel !== current;
      if (!panel.hidden) this.heading.textContent = panel.dataset.title;
    });
    this.dialog.querySelector('[data-ww-menu-back]').hidden = this.stack.length < 2;
    this.body.scrollTop = this.scrollPositions.get(current) || 0;
    this.heading.focus({preventScroll:true});
  }

  close() {
    if (!this.dialog.open) return;
    const state = history.state?.wwMobileMenu;
    if (state?.path === location.pathname) {
      this.closingHistory = true;
      history.go(-this.stack.length);
    }
    this.finishClose();
  }

  restorePage() {
    if (this.dialogAnchor) {
      this.dialogAnchor.replaceWith(this.dialog);
      this.dialogAnchor = null;
    }
    Object.assign(document.body.style, this.savedBody);
    window.scrollTo({ top: this.pageScroll, behavior: 'instant' });
  }

  finishClose(restoreFocus = true) {
    if (!this.dialog?.open) return;
    this.dialog.close();
    this.trigger.setAttribute('aria-expanded', 'false');
    document.querySelectorAll('[data-ww-open-menu-panel]').forEach(e=>e.setAttribute('aria-expanded','false'));
    this.desktopDiscovery = false;
    this.dialog.removeAttribute('data-ww-desktop-discovery');
    this.restorePage();
    if (restoreFocus) {
      const header = this.closest('#header-component');
      const target = [this.returnFocus, this.trigger, ...(header?.querySelectorAll('a[href],button') || [])]
        .find(element => element?.isConnected && element !== document.body && element !== document.documentElement && element.getClientRects().length);
      target?.focus({preventScroll:true});
    }
  }
}
if (!customElements.get('ww-mobile-navigation')) customElements.define('ww-mobile-navigation', WonderWallMobileNavigation);
